import { useRouter } from "next/router"
import { dehydrate, QueryClient } from "react-query"
import { twMerge } from "tailwind-merge"
import {
  Header,
  GuidesList,
  CityPlaylist,
  ContentTemplate,
  CityBoard,
  PureAds,
  FooterWithDevil,
  HeadMeta,
  useSpotify,
  useSmartBanner,
} from "components"
import { ReactSVG } from "react-svg"
import {
  getHost,
  getCityUrl,
  getPlural,
  LocaleContext,
  DEFAULT_LOCALE,
} from "lib"
import {
  useGetPageData,
  getCities,
  getCity,
  getSmartBanner,
  useGetCity,
  useGetCommonTranslations,
  prefetchPageData,
  prefetchCommonTranslations,
  useGetCities,
  prefetchCities,
  prefetchArticlesByCity,
} from "api"
import { SMART_BANNER_PATH_VALUES, purgeCityPageFiles } from "lib/isr"
import type { GetStaticProps, GetStaticPaths } from "next"
import type { SmartBannerPathValues } from "lib/isr"
import type { SmartBannerData } from "api"

import CloudTop from "public/images/cloud-playlist.svg"
import CloudTopMobile from "public/images/cloud-mobile.svg"

interface CityPageProps {
  url: string
  smartBannerData?: SmartBannerData
}

type CityParsedUrlQuery = {
  slug: string,
  smartBanner: SmartBannerPathValues,
}

export const getStaticProps: GetStaticProps<{}, CityParsedUrlQuery> = async(ctx) => {
  const { locale, params } = ctx
  const slug = params?.slug || ""

  const queryClient = new QueryClient()

  const city = await queryClient.fetchQuery(["city", slug, locale], () =>
    getCity(slug?.toString(), locale),
  )
  if (!city) {
    await purgeCityPageFiles(slug, locale)

    return {
      redirect: {
        permanent: false,
        destination: "/",
      },
    }
  }

  await Promise.all([
    prefetchPageData(queryClient, "city", locale),
    prefetchCommonTranslations(queryClient, locale),
    prefetchCommonTranslations(queryClient, DEFAULT_LOCALE),
    prefetchCities(queryClient, locale),
    prefetchArticlesByCity(queryClient, city.id, locale),
  ])

  const smartBannerData = await getSmartBanner(locale)

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      url: getHost(locale) + getCityUrl(slug as string),
      smartBannerData: params?.smartBanner === "smart-banner-disabled" ? null : smartBannerData,
    },
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const allCities = await getCities("all")
  const paths: any[] = []

  allCities.forEach(({ slug, locale, localizations }) => {
    SMART_BANNER_PATH_VALUES.forEach(smartBanner => {
      paths.push({ params: { slug, smartBanner }, locale  })
    })
  })

  return {
    paths,
    fallback: true,
  }
}

export default function CityPage(props: CityPageProps) {
  const { url, smartBannerData } = props
  const {
    query: { slug },
  } = useRouter()
  const { data } = useGetPageData("city")
  const { data: translations } = useGetCommonTranslations()
  const { isLoading, data: city } = useGetCity(slug?.toString())
  const { data: cities = [] } = useGetCities()
  const isSpotifyAvailable = useSpotify()
  const smartBanner = useSmartBanner(smartBannerData)

  if (isLoading || !data || !city) {
    return null
  }

  const showSpotify =
    isSpotifyAvailable &&
    city.spotify_playlist &&
    city.spotify_playlist.url &&
    city.spotify_playlist.image &&
    city.spotify_playlist.title

  const cloudColor = showSpotify ? "text-gray-50" : "text-white"
  const articlesCount = city.articles.length

  const boardCities = cities.filter(
    (city) => city.slug !== slug && city.articles.length,
  )
  const title = data.locales["tab.title"]
    ? data.locales["tab.title"] + city.name
    : city.name

  return (
    <LocaleContext.Provider value={data}>
      <HeadMeta meta={city.meta} url={url} title={title} />
      {smartBanner}
      <div className={twMerge(smartBanner && "mt-20 md:mt-[52px]")}>
        <Header theme="dark" currentCity={city} variant="menu" />
      </div>

      <ContentTemplate>
        <section className="relative pt-14 lg:pt-36 ">
          <p className="break-words font-serif text-5xl uppercase md:text-7xl">
            {city.name}
          </p>
          <h1 className="mt-4 mb-10 font-sans text-xl md:mt-6 md:max-w-xl md:text-2xl lg:mb-[104px]">
            {city.description}
          </h1>
          <div className="items-end md:flex md:flex-row-reverse md:justify-between lg:flex-row">
            {city.image && (
              <ReactSVG
                className={`text-${city.color} grid items-end md:h-[246px] md:w-[410px] lg:absolute lg:right-0 lg:h-[300px] lg:w-[500px]`}
                src={city.image.url}
              />
            )}
            <p className="mt-10 mb-4 font-sans text-sm uppercase lg:mt-0">
              {`${articlesCount} ${
                translations?.locales[`sexcursion.${getPlural(articlesCount)}`]
              }`}
            </p>
          </div>
        </section>
      </ContentTemplate>

      <ContentTemplate className="md:px-6 lg:px-16">
        <GuidesList city={city} />
      </ContentTemplate>

      <ContentTemplate className={`relative mt-[138px] lg:mt-44 ${cloudColor}`}>
        <CloudTop className="hidden md:absolute md:top-[-55px] md:right-12 md:block md:h-[56px] lg:right-64 lg:-top-[71px] lg:h-18" />
        <CloudTopMobile className="absolute top-[-49px] right-0 h-[51px] md:hidden" />
      </ContentTemplate>

      {showSpotify && <CityPlaylist city={city} />}

      <PureAds />

      <ContentTemplate className="pt-14 pb-16 lg:pt-20 lg:pb-20">
        <p className="mb-4 font-sans text-xl md:mb-2 lg:mb-4">
          {data.locales["city_board.title"]}
        </p>
        <CityBoard cities={boardCities} />
      </ContentTemplate>

      <FooterWithDevil />
    </LocaleContext.Provider>
  )
}
